import { z } from "zod";
/**
 * Ролей ровно две. `admin` решает и настраивает (гипотезы, вердикты, учётки,
 * пороги), `user` работает руками (заливка крео, вариации, запуск и стоп,
 * заметки). Прежние `motion`/`ua` схлопнуты в `user` миграцией 0002 — они
 * различались не доступом к данным, а привычкой, и держать три ветки проверок
 * стоило дороже, чем давало.
 */
export const roles = ["admin", "user"];
export const roleSchema = z.enum(roles);
export const roleLabels = { admin: "Админ", user: "Пользователь" };
export const creativeStates = [
    "production",
    "ready_for_upload",
    "live",
    "verdict_pending",
    "paused_manual",
    "live_test2",
    "verdict_pending_test2",
    "archived",
    "version_spawned",
    "scaled",
    "fatigued",
    "retired",
    // Лузер — финал, зеркальный виннеру: тест провален, бюджет сюда не льём.
    // Ставит человек, решением в вердикте или всей гипотезой разом.
    "lost"
];
export const creativeStateSchema = z.enum(creativeStates);
/**
 * «Середняка» и «не крео» здесь больше нет: пороги стали абсолютными, и всё,
 * что не сломано и не победитель, остаётся без вердикта до решения человека
 * (обычно — возврат гипотезы в бэклог). Исторические записи вычищены
 * миграцией 0017.
 */
export const verdicts = [
    "winner",
    "hook_broken",
    "body_broken",
    "close_broken",
    "underdelivered"
];
export const verdictSchema = z.enum(verdicts);
export const hypothesisStatuses = [
    "backlog",
    "rework_safe",
    "live",
    "proven",
    "split",
    "frozen",
    "killed",
    "lost"
];
export const reviewKinds = [
    "verdict",
    "learning",
    "wave_draft",
    "harvest",
    "movement",
    "underdelivery",
    "validator_alert",
    "fatigue_alert",
    "taxonomy"
];
export const deliveryStatuses = [
    "active", "paused", "pending_review", "rejected", "deleted", "unknown"
];
/** Статус объявления в кабинете словами UA: «отклонено модерацией» важнее любого другого чипа. */
export const deliveryStatusLabels = {
    active: "крутится",
    paused: "пауза в кабинете",
    pending_review: "на модерации",
    rejected: "отклонено модерацией",
    deleted: "удалено из кабинета",
    unknown: "статус неизвестен"
};
/**
 * Метка Creative Quarantine из marketing-v2: `suspect` ставит сама система
 * (3+ отклонённых объявления по всем кабинетам), `quarantined` — человек в
 * админке. Порог и белые списки живут там, доска только читает.
 */
export const quarantineMarks = ["suspect", "quarantined"];
/**
 * Модерация крео по всем кабинетам (GEN-2250): сколько объявлений под его
 * именем запущено и сколько из них модерация хоть раз отклонила. Реджекты —
 * по истории, как считает карантин: одобренное после отказа из счёта не уходит.
 */
export const moderationSchema = z.object({
    launched: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    rejectedAccounts: z.array(z.string()),
    quarantine: z.enum(quarantineMarks).nullable()
});
export const moderationStatusLabels = {
    quarantine: "петушиный угол",
    unusable: "не используем"
};
/**
 * Одна формула на бек (карточка в Разбор при входе в статус) и доску (чип):
 * разъехавшиеся копии показали бы «не используем» там, где Разбор молчит.
 */
export function moderationVerdict(moderation, thresholds) {
    if (!moderation || moderation.launched === 0)
        return { status: null, tone: "idle", rate: 0 };
    const rate = moderation.rejected / moderation.launched;
    const status = moderation.launched >= thresholds.minLaunches && rate > thresholds.banRate ? "unusable"
        : moderation.quarantine ? "quarantine"
            : null;
    const tone = status ? "bad" : moderation.rejected > 0 ? "warn" : "clean";
    return { status, tone, rate };
}
/**
 * Сколько XP нужно на следующий уровень. Одна формула на бек (awardXp) и бар
 * в шапке: разъехавшиеся копии показывали бы прогресс к несуществующей цели.
 */
export function xpThreshold(level) {
    return 260 + level * 60;
}
export const producers = ["claude", "motion", "julia"];
/** Лейблы — ровно те, что видит UA в админке кабинета. */
export const mediaLibraryStatusLabels = {
    draft: "new",
    review: "in review",
    live: "on traffic",
    rejected: "rejected",
    archived: "archived"
};
/**
 * Сырые числители за всю жизнь крео. Нужны потому, что концепт нельзя сложить
 * из рейтов: сумма долей — не доля суммы. Без них строка гипотезы могла бы
 * только усреднять средние, и один удачный крео представлял бы всю гипотезу.
 */
export const metricTotalsSchema = z.object({
    spend: z.number().nonnegative(),
    impressions: z.number().nonnegative(),
    clicks: z.number().nonnegative(),
    videoPlays: z.number().nonnegative(),
    videoViews3s: z.number().nonnegative(),
    videoThruplays: z.number().nonnegative(),
    videoP100: z.number().nonnegative(),
    realSubscriptions: z.number().nonnegative(),
    realPurchases: z.number().nonnegative()
});
export const metricsSchema = z.object({
    spend: z.number().nonnegative(),
    impressions: z.number().int().nonnegative(),
    // Верхняя граница снята намеренно. У Meta ctr считается по link clicks, и он
    // изредка легитимно превышает показы; hook/hold переживают ре-атрибуцию.
    // Раньше такая строка не проходила разбор и крео молча оставалось без метрик
    // — потерять статистику хуже, чем показать 104%.
    hookRate: z.number().nonnegative(),
    holdRate: z.number().nonnegative(),
    ctr: z.number().nonnegative(),
    cpa: z.number().nonnegative().nullable(),
    cpm: z.number().nonnegative().nullable(),
    frequency: z.number().nonnegative().nullable(),
    curve: z.array(z.number().min(0).max(100)),
    measuredAt: z.string(),
    totals: metricTotalsSchema.optional(),
    deliveryStatus: z.enum(deliveryStatuses).optional(),
    statusChangedAt: z.string().nullable().optional(),
    statusDetail: z.string().nullable().optional(),
    moderation: moderationSchema.optional()
});
/**
 * Пустая строка фида не должна стирать историю крео.
 *
 * Кабинет может замолчать по-разному — его заархивировали на стороне
 * маркетинга, отвалился токен, сборщик отдал пустой ответ, — и наружу это
 * приезжает одинаково: spend=0, impressions=0 и свежий measured_at. По
 * last-write-wins такая строка выглядит новее накопленных цифр и затирает их
 * (а вместе с ними и суточный ряд, который заменяется окном целиком).
 *
 * Крео, у которого уже были показы, обратно в нули не возвращается: открутка
 * — событие необратимое. Поэтому такую строку мы пропускаем и говорим об этом
 * в Разборе, а не применяем молча.
 */
export function blanksExistingMetrics(previous, next) {
    const arrivedEmpty = next.spend === 0 && next.impressions === 0;
    const hadNumbers = previous.spend > 0 || previous.impressions > 0;
    return arrivedEmpty && hadNumbers;
}
export const conceptPeriods = ["7d", "14d", "all"];
export const createHypothesisSchema = z.object({
    statement: z.string().trim().min(8).max(500),
    // Рабочее имя механики. Необязательное здесь, потому что форма доски его не
    // спрашивает, а карточка умеет падать на statement; MCP-инструменты требуют
    // его обязательно — там имя всегда есть кому придумать.
    title: z.string().trim().min(2).max(80).optional(),
    pain: z.string().trim().min(1),
    desire: z.string().trim().min(1),
    angle: z.string().trim().min(1),
    source: z.string().trim().default("team"),
    priority: z.enum(["P0", "P1", "P2", "P3"]).default("P2"),
    judge: z.enum(["hook_rate", "hold_rate", "ctr", "cpa"]).default("cpa"),
    /** Автор гипотезы — уезжает автором концепта в Marketing v2. */
    author: z.string().trim().min(2).max(128).default("Andrey"),
    /**
     * Продукт, на который льются крео гипотезы (Lovmia, Lovmia Shorts, …).
     * Допустимые значения — справочник settings.products; сервер валидирует по
     * нему, схема лишь ограничивает форму. Свойство гипотезы, а не крео: пара
     * меряет одно утверждение на одном аппсторе-листинге.
     */
    product: z.string().trim().min(2).max(64).default("Lovmia")
});
export const takeIntoWaveSchema = z.object({
    c1Producer: z.enum(producers).default("claude"),
    c2Producer: z.enum(producers).default("motion"),
    /**
     * Оси формы задаются на ПАРУ, а не на крео: пара меряет одно утверждение,
     * поэтому приём (`hook`), способ подачи (`delivery`) и темп держатся общими,
     * а расходятся C1 и C2 производителем, форматом и осью вариации. Разный
     * приём внутри пары делает расхождение метрик нечитаемым.
     *
     * Не передали — останется `unassigned`/`scripted_scene`/`medium`. Это
     * честная метка «приём не объявлен», и волна из таких крео на доске видна
     * как однообразная, а не выглядит разнообразной по одним лишь болям.
     */
    hook: z.string().trim().min(1).optional(),
    delivery: z.string().trim().min(1).optional(),
    pacing: z.string().trim().min(1).optional(),
    /**
     * Формат по умолчанию выводился из producer'а (claude → slideshow), поэтому
     * у C1 стоял `slideshow` даже когда в карточке лежал проговорённый ролик, и
     * агрегация «слайдшоу против видео» считалась по выдуманному признаку.
     * Дефолт сохранён ради обратной совместимости, но его можно перебить —
     * задним числом формат не меняется: он вшит в `ad_name` и на нём матчинг
     * метрик.
     */
    c1Format: z.enum(["seedance_video", "slideshow", "static"]).optional(),
    c2Format: z.enum(["seedance_video", "slideshow", "static"]).optional(),
    deadline: z.string().nullable().optional()
});
export const addNoteSchema = z.object({
    text: z.string().trim().min(2).max(1000),
    pain: z.string().trim().nullable().optional(),
    ref: z.string().trim().nullable().optional()
});
export const settingsSchema = z.object({
    gateSpend: z.number().min(1),
    emergencyStopSpend: z.number().min(1),
    /** Доля медианы, ниже которой CTR считается сломом клоуза. Hook и hold судятся абсолютными порогами ниже. */
    brokenRatio: z.number().min(0.1).max(1),
    emergencyHookRatio: z.number().min(0.1).max(1),
    /** Абсолютные пороги оценки: hook/hold ниже — красный и слом, выше — зелёный. */
    hookRateMin: z.number().min(0).max(1),
    holdRateMin: z.number().min(0).max(1),
    /** Победитель: гейт пройден и CPS (spend / real_subscriptions) ниже этой суммы в долларах. */
    winnerCps: z.number().min(1),
    /** Бюджет теста 2 (скейл-прогона) на одно крео: столько дельты спенда набирает live_test2 до «Вердикт (тест 2)». */
    test2Spend: z.number().min(1),
    exploreShare: z.number().min(0).max(1),
    digestMonday: z.string(),
    digestFriday: z.string(),
    medians: z.object({
        hookRate: z.number().min(0).max(1),
        holdRate: z.number().min(0).max(1),
        ctr: z.number().min(0).max(1),
        cpa: z.number().min(0),
        manual: z.boolean()
    }),
    /**
     * Справочник продуктов, на которые льются крео (Lovmia, Lovmia Shorts, …).
     * Закрытый список вместо свободной строки: опечатка «lovmia/Lovmia» развела
     * бы один продукт на два фильтра. Новый продукт заводится здесь, без деплоя.
     */
    products: z.array(z.string().trim().min(2).max(64)).min(1).default(["Lovmia", "Lovmia Shorts"]),
    /**
     * Генерация через провайдера. Ключ спрятан на сервере, поэтому единственный
     * реальный ограничитель расхода — этот потолок; 0 означает «генерация
     * выключена», а не «без ограничений».
     */
    generation: z.object({
        dailyLimitUsd: z.number().min(0).max(10_000)
    }).default({ dailyLimitUsd: 50 }),
    /**
     * Локали карточками на доске (плоская структура) вместо флажков внутри крео.
     * Настройка, а не константа: пока локалей мало, колонки чище с флажками, а
     * когда гео становится ось работы — локаль обязана стоять в своей колонке,
     * иначе «сделай файл для ES-MX» не видно из «Продакшна».
     */
    flatLocalizations: z.boolean().default(true),
    /**
     * Статус «не используем» (GEN-2250): доля отклонённых объявлений по всем
     * кабинетам строго выше banRate. Пока запусков меньше minLaunches, доля не
     * судится — один реджект из одного запуска это ещё не 100%.
     * Порог «петушиного угла» здесь не живёт: его считает карантин в API.
     */
    moderation: z.object({
        banRate: z.number().min(0).max(1),
        minLaunches: z.number().int().min(1).max(1000)
    }).default({ banRate: 0.2, minLaunches: 5 }),
    /**
     * Уведомления в Slack. Канал — обычная настройка; токен бота живёт отдельно,
     * шифротекстом в provider_credentials, и наружу не отдаётся никогда.
     * Пустой канал выключает отправку.
     */
    slack: z.object({
        channel: z.string().trim()
    }).default({ channel: "C0BUGQ939AP" })
});
export const generationStatuses = ["queued", "processing", "completed", "failed"];
export const characterStatuses = ["active", "retired"];
export const saveCharacterSchema = z.object({
    name: z.string().trim().min(2).max(80),
    description: z.string().trim().max(1000).nullable().optional()
});
export const updateCharacterSchema = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    status: z.enum(characterStatuses).optional()
});
export const masterScriptSchema = z.object({
    arc: z.string().trim().min(3),
    beats: z.array(z.object({
        act: z.number().int().min(1),
        t: z.string(),
        beat: z.string(),
        meaning: z.string().optional(),
        overlay: z.string().optional()
    })).min(1),
    locks: z.array(z.string()).default([])
});
/**
 * Локали — как в рекламном кабинете (marketing-v2): строго `ll_CC`. Список
 * зеркалит справочник админки; язык без региона недопустим — кабинет
 * интерпретирует его неоднозначно (pt → pt_PT, а реклама идёт на pt_BR).
 */
export const localePattern = /^[a-z]{2}_[A-Z]{2}$/;
export const supportedLocales = [
    "en_US", "es_MX", "es_ES", "pt_BR", "de_DE", "fr_FR", "it_IT", "ja_JP", "ko_KR", "nl_NL", "pl_PL", "tr_TR"
];
export const localeLabels = {
    en_US: "English · US", es_MX: "Español · México", es_ES: "Español · España", pt_BR: "Português · Brasil",
    de_DE: "Deutsch", fr_FR: "Français", it_IT: "Italiano", ja_JP: "日本語", ko_KR: "한국어",
    nl_NL: "Nederlands", pl_PL: "Polski", tr_TR: "Türkçe"
};
/** `es_MX` → `ES-MX`: так локаль входит в id и ad_name (`_` внутри id спутался бы с `__`-разделителем имени). */
export function localeTag(locale) {
    return locale.replace("_", "-").toUpperCase();
}
/** Флажок локали на доске: `AR-SA`, у второй и дальше версии — `AR-SA V2`. */
export function localizationTag(item) {
    return item.version > 1 ? `${localeTag(item.locale)} V${item.version}` : localeTag(item.locale);
}
export const localizationStates = ["production", "ready_for_upload", "live", "archived"];
export const localizationStateLabels = {
    production: "нет файла", ready_for_upload: "готово к заливу", live: "в кабинете", archived: "архив"
};
/**
 * Ассеты плейсментов: тот же креатив в другой пропорции под другой плейсмент
 * ТОГО ЖЕ объявления. Основной файл карточки — 9:16 (Reels, Stories); 1:1 и
 * 4:5 — лента (Feed), в кабинете это «настройка ассета по плейсменту» одного
 * объявления. Поэтому у ассета нет ни своего имени, ни своих цифр, ни своей
 * карточки: объявление одно, ad_name один, метрики общие. Не локаль (другой
 * язык — другое объявление) и не версия (переделка — тоже другое).
 */
export const placementAspects = ["1:1", "4:5"];
export const placementAspectLabels = {
    "1:1": "квадрат · лента",
    "4:5": "вертикаль · лента"
};
/** `1:1` → `1x1`: двоеточие не живёт ни в URL, ни в имени файла. */
export function placementAspectSlug(aspect) {
    return aspect.replace(":", "x");
}
export function placementAspectFromSlug(slug) {
    const aspect = slug.replace("x", ":");
    return placementAspects.includes(aspect) ? aspect : null;
}
/**
 * Имя файла ассета для UA: `H_130_V1_C1_AR_SA__own_pace__static__claude__w43__1x1.png`.
 * Имя объявления целиком плюс пропорция — по нему в загрузках видно, к какому
 * объявлению и в какой плейсмент картинка. Матчинг метрик на это имя не
 * опирается: он идёт по имени объявления.
 */
export function placementAssetFilename(adName, aspect, extension) {
    return `${adName}__${placementAspectSlug(aspect)}${extension}`;
}
export const placementAssetSchema = z.object({
    aspect: z.enum(placementAspects)
});
export const addLocalizationSchema = z.object({
    locale: z.string().trim().regex(localePattern, "Локаль — в формате ll_CC, например es_MX")
});
/** Новая версия локали: что переделали — обязательно, иначе версия неотличима от копии. */
export const addLocalizationVersionSchema = z.object({
    note: z.string().trim().min(3, "Напишите, что переделали в этой версии").max(500)
});
export const updateHypothesisConceptSchema = z.object({
    title: z.string().trim().min(2).max(80).nullable().optional(),
    statement: z.string().trim().min(8).max(500).optional(),
    description: z.string().trim().min(8).max(1000).nullable().optional(),
    masterScript: masterScriptSchema.nullable().optional(),
    template: z.string().trim().max(6000).nullable().optional(),
    tone: z.string().trim().max(40).nullable().optional(),
    batch: z.enum(["exploit", "explore"]).nullable().optional(),
    /** Автор правится в любой момент: это подпись, а не ось измерения. */
    author: z.string().trim().min(2).max(128).nullable().optional(),
    /**
     * Продукт правится в любой момент — в ad_name он не вшит, метрики по нему не
     * матчатся. Сервер сверяет значение со справочником settings.products.
     */
    product: z.string().trim().min(2).max(64).optional(),
    // Таксономия правится только у гипотезы без крео: pain вшит в ad_name
    // (`H-041-C1__loneliness__…`), и правка задним числом рассинхронит матчинг
    // метрик. Проверку делает сервер, форма лишь дизейблит поля.
    pain: z.string().trim().min(1).optional(),
    desire: z.string().trim().min(1).optional(),
    angle: z.string().trim().min(1).optional(),
    priority: z.enum(["P0", "P1", "P2", "P3"]).optional(),
    judge: z.enum(["hook_rate", "hold_rate", "ctr", "cpa"]).optional(),
    /**
     * Что переработали относительно родителя. Правится как обычный текст: связь
     * с родителем — факт (её задаёт id), а формулировка отличия уточняется по
     * ходу, как и утверждение. У гипотезы без родителя поля нет — сервер откажет.
     */
    variationNote: z.string().trim().min(8).max(500).nullable().optional()
});
/**
 * Ручной статус гипотезы. Живые статусы (`live`, `split`, `proven`) считаются
 * из вердиктов её крео и руками не ставятся — иначе доска разъедется с
 * реальностью. Человеку остаются три: убрать в холодильник, закрыть и вернуть
 * в бэклог (`backlog` снимает ручной статус, дальше снова решает автоматика).
 */
export const manualHypothesisStatuses = ["backlog", "rework_safe", "frozen", "killed"];
export const setHypothesisStatusSchema = z.object({
    status: z.enum(manualHypothesisStatuses)
});
/**
 * Объединение двух гипотез: все крео источника переезжают в цель (получают её
 * следующие номера и `ad_name` от её pain), источник удаляется. Только пока
 * ни одно крео источника не стало фактом в кабинете (спенд, вердикт, метрики).
 */
export const mergeHypothesesSchema = z.object({
    sourceId: z.string().trim().min(3).max(60)
});
/**
 * Разделение: часть крео уезжает в новую гипотезу, которая наследует таксономию,
 * автора, судью и приоритет исходной. Имя механики — обязательное: это
 * заголовок новой карточки, и «копия H-025» читаться на доске не должна.
 */
export const splitHypothesisSchema = z.object({
    creativeIds: z.array(z.string().trim().min(3).max(60)).min(1).max(100),
    title: z.string().trim().min(2).max(80),
    statement: z.string().trim().min(8).max(500).optional(),
    description: z.string().trim().min(8).max(1000).nullable().optional()
});
/**
 * Вариация гипотезы: `H-029-V2` — та же линия, переписанная по одной оси.
 *
 * Между дополнительным исполнением (`H-029-C3`, тот же концепт другим роликом)
 * и новой гипотезой (`H-030`, другой концепт) стояла пустота: переработанное
 * утверждение — уже не крео, но ещё и не новая механика. Вариация занимает
 * это место: своя карточка со своими крео, своим гейтом и своим вердиктом, но
 * с именем и ссылкой, по которым видно, из чего она выросла.
 *
 * `note` обязателен — вариация без «что именно переработали» неотличима от
 * копии, а ради этой строки ветка и заводится: по ней читается эволюция.
 * Остальное наследуется от родителя и переопределяется по желанию.
 */
export const createVariationSchema = z.object({
    note: z.string().trim().min(8).max(500),
    title: z.string().trim().min(2).max(80).optional(),
    statement: z.string().trim().min(8).max(500).optional(),
    description: z.string().trim().min(8).max(1000).nullable().optional(),
    priority: z.enum(["P0", "P1", "P2", "P3"]).optional(),
    judge: z.enum(["hook_rate", "hold_rate", "ctr", "cpa"]).optional(),
    author: z.string().trim().min(2).max(128).optional()
});
/**
 * Корень линии эволюции: `H-029-V3` → `H-029`. Версии нумеруются от корня
 * плоско (V2, V3, …), а не вложенно (`H-029-V2-V2`): длина id уходит в
 * `ad_name`, а «версия версии» на доске всё равно читается как следующий шаг
 * одной линии. Кто чей прямой предок, хранит `parentId`.
 */
export function hypothesisLineageRoot(id) {
    return id.replace(/-V\d+$/, "");
}
/** Перенос нескольких крео в существующую гипотезу — в переданном порядке. */
export const moveCreativesSchema = z.object({
    creativeIds: z.array(z.string().trim().min(3).max(60)).min(1).max(100)
});
export const updateVariationSchema = z.object({
    variationTitle: z.string().trim().min(2).max(80).nullable().optional(),
    /**
     * Кто собрал крео (Claude / Motion / Julia). Producer вшит в `ad_name`, поэтому
     * у крео, которое ещё не выходило с доски, имя пересобирается; у заведённого
     * в кабинете меняется только подпись — имя остаётся тем, под которым живёт
     * объявление.
     */
    producer: z.enum(producers).optional(),
    script: z.string().trim().max(6000).nullable().optional(),
    axis: z.string().trim().max(80).nullable().optional(),
    character: z.string().trim().max(300).nullable().optional(),
    characterId: z.string().trim().min(1).nullable().optional(),
    slots: z.record(z.string(), z.string()).nullable().optional(),
    qaVerdict: z.enum(["PASS", "FAIL", "FIXABLE"]).nullable().optional(),
    qaScore: z.number().min(0).max(10).nullable().optional(),
    factoryRef: z.string().trim().max(60).nullable().optional(),
    /** Свободный комментарий по крео — заменил обязательное замечание колонки «Переделать». */
    comment: z.string().trim().max(1000).nullable().optional()
});
export const eventSources = ["web", "mcp", "system"];
/**
 * Человекочитаемые названия событий для тостов и ленты. Ключ — `event` из
 * `audit_events`; неизвестный ключ фронт показывает как есть, чтобы новое
 * событие на бэке не роняло UI.
 */
export const eventLabels = {
    "hypothesis.created": "новая гипотеза",
    "hypothesis.taken_into_wave": "гипотеза взята в работу",
    "hypothesis.concept_updated": "обновлён концепт",
    "hypothesis.status_changed": "статус гипотезы изменён",
    "hypothesis.deleted": "гипотеза удалена",
    "creative.added": "добавлено крео",
    "creative.variation_updated": "обновлена вариация",
    "creative.deleted": "крео удалено",
    "creative.matched": "крео в открутке",
    "creative.paused": "крео остановлено",
    "creative.scale": "крео в скейле",
    "creative.iterate": "создана итерация",
    "creative.archive": "крео в архиве",
    "creative.kill": "крео убито",
    "creative.restart": "крео перезапущено",
    "file.registered": "загружено видео",
    "character.created": "новый персонаж",
    "character.updated": "персонаж изменён",
    "character.image_attached": "загружен лист персонажа",
    "character.deleted": "персонаж удалён",
    "hypothesis.character_linked": "персонаж в гипотезе",
    "hypothesis.character_unlinked": "персонаж снят с гипотезы",
    "creative.registered": "крео заведено в кабинете",
    "creative.media_delivered": "видео уехало в медиатеку",
    "creative.recalled": "крео отозвано из медиатеки",
    "creative.renumbered": "номер крео сдвинут",
    "creative.moved": "крео перенесено в другую гипотезу",
    "creative.producer_changed": "сменён producer крео",
    "localization.added": "добавлена локаль",
    "localization.file_attached": "загружен файл локали",
    "localization.matched": "локаль в кабинете",
    "localization.registered": "локаль заведена в кабинете",
    "localization.media_delivered": "видео локали уехало в медиатеку",
    "localization.recalled": "локаль отозвана из медиатеки",
    "localization.deleted": "локаль удалена",
    "hypothesis.creatives_reordered": "порядок крео изменён",
    "hypothesis.merged": "гипотезы объединены",
    "hypothesis.split": "гипотеза разделена",
    "note.added": "новая заметка",
    "note.promoted": "заметка стала гипотезой",
    "note.discarded": "заметка убрана",
    "learning.added": "новый learning",
    "learning.approve": "learning принят",
    "learning.reject": "learning отклонён",
    "review.resolved": "разбор закрыт",
    "taxonomy.proposed": "предложен термин словаря",
    "taxonomy.approved": "термин принят в словарь",
    "taxonomy.rejected": "термин отклонён",
    "taxonomy.added": "термин добавлен в словарь",
    "taxonomy.updated": "термин словаря изменён",
    "settings.updated": "изменены настройки",
    "integration.key_updated": "обновлён ключ провайдера",
    "integration.key_removed": "удалён ключ провайдера",
    "generation.started": "запущена генерация",
    "generation.completed": "генерация готова",
    "generation.failed": "генерация упала",
    "metrics.synced": "метрики обновлены",
    "hypothesis.test1_tested": "гипотеза протестилась (тест 1)",
    "team.member_added": "новый участник",
    "team.member_updated": "участник изменён",
    "team.member_removed": "участник удалён"
};
/* ==========================================================================
 * Имя объявления: что из наших слов уезжает в рекламный кабинет
 * ========================================================================== */
/**
 * Корни, которых не должно быть в имени объявления.
 *
 * Имя крео — не внутренняя подпись: оно уезжает в marketing-v2 и становится
 * именем объявления в кабинете, то есть строкой, которую видит модерация
 * площадки. Слаг боли попадал туда как есть, и на доске оказались имена вида
 * `H-084-C1__sexual_frustration__…`: наш исследовательский словарь называет
 * состояние человека прямо (это его работа), а в кабинете такая строка читается
 * как таргетинг по психологической уязвимости — ровно то, за что снимают
 * объявления и режут аккаунты.
 *
 * Список намеренно короткий и из корней, а не из слов: `frustrat` ловит и
 * `frustration`, и `frustrated`. Корни подобраны так, чтобы не задевать
 * нейтральный словарь (поэтому нет ни `fat` — он внутри `fatigue`, ни `anal` —
 * внутри `analytics`). Кириллица здесь в транслите: слаги латинские, а имя
 * файла в медиатеке собирается транслитерацией русского названия вариации.
 *
 * Это не фильтр смысла: тема крео может быть какой угодно, ограничение — только
 * на слова, которыми мы её ПОДПИСЫВАЕМ снаружи.
 */
export const adUnsafeRoots = [
    // Секс и explicit
    "sex", "seks", "porn", "nsfw", "erotic", "nude", "naked", "golaya", "horny", "kink", "fetish",
    "orgasm", "masturb", "lust", "pohot", "intim", "hookup", "escort", "trah",
    // Психологическое состояние и уязвимость
    "lonel", "odinochestv", "depress", "suicid", "anxiet", "anxious", "trevozh", "trauma",
    "shame", "styd", "humiliat", "inadequa", "reject", "frustrat", "desperat", "insecur",
    "addict", "obsess", "incel", "virgin", "ghosting",
    // Возраст и безопасность
    "underage", "minor", "teen", "schoolgirl", "loli", "child",
    // Здоровье, вещества, насилие
    "herpes", "drug", "cocain", "heroin", "rape", "abuse", "violen",
    // Личные обстоятельства
    "izmena", "cheating", "divorc"
];
/**
 * Нормализация перед сверкой: регистр вниз, все разделители прочь. Так
 * `First_Move` и `first-move` проверяются одинаково, а `s_e_x` не проезжает
 * мимо списка. Побочный эффект — корень может собраться на стыке двух
 * безобидных слов; это осознанный перекос в сторону строгости, автору просто
 * придётся подобрать другое слово.
 */
function adNameNormalize(value) {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}
/** Найденный корень (для текста ошибки) или null, если слово безопасно. */
export function adUnsafeMatch(value) {
    const normalized = adNameNormalize(value);
    return adUnsafeRoots.find((root) => normalized.includes(root)) ?? null;
}
/**
 * Оси, чей слаг уезжает в `ad_name` как есть. Это технические перечисления
 * (`seedance_video`, `motion`), они и должны читаться в кабинете буквально —
 * поэтому их слаг обязан проходить блоклист. У боли путь другой: у неё есть
 * `adCode` (см. ниже), и внутренний слаг остаётся честным.
 */
export const adNameAxes = ["format", "producer"];
/**
 * Двойное подчёркивание внутри сегмента развалило бы имя на лишние части: в
 * имени оно РАЗДЕЛИТЕЛЬ, и `first__move` превратил бы пятисегментное имя в
 * шестисегментное — парсер либо не прочитает его, либо прочитает не так.
 * Одинарные внутри слов (`first_move`) остаются нормой.
 */
export const adNameSeparatorMessage = "Двойное подчёркивание — разделитель сегментов имени объявления, внутри слова его быть не может: first_move, не first__move";
/** Код в имени объявления: короткий, латиница, и без слов из блоклиста. */
export const adCodeSchema = z.string().trim().min(2).max(24)
    .regex(/^[a-z][a-z0-9_]*$/, "Код — латиница в нижнем регистре, слова через подчёркивание: first_move")
    .refine((value) => !value.includes("__"), adNameSeparatorMessage)
    .superRefine((value, ctx) => {
    const root = adUnsafeMatch(value);
    if (root) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `«${value}» не годится для имени объявления: в нём читается «${root}». `
                + "Код уезжает в кабинет и попадает под модерацию площадки — подберите нейтральное слово о теме крео, а не о состоянии человека."
        });
    }
});
/**
 * Резервный код, когда у боли его почему-то нет (термин из старых данных,
 * которого нет в словаре). Безопасный слаг отдаём как есть — читаемость
 * дороже; опасный заменяем стабильным `topic_xxxx`, чтобы имя осталось
 * уникальным и воспроизводимым, а не превратилось в общий `other`.
 */
export function adSafeFallbackCode(slug) {
    if (!adUnsafeMatch(slug))
        return slug;
    let hash = 0x811c9dc5;
    for (const char of slug) {
        hash = (hash ^ char.charCodeAt(0)) * 0x01000193 >>> 0;
    }
    return `topic_${hash.toString(16).padStart(8, "0").slice(0, 4)}`;
}
/**
 * Версия формата имени.
 *
 * - `1` — дефисный id (`H-041-C2__…`). Только у имён, ставших фактом в кабинете
 *   до 2026-09-17; собрать её заново нельзя, валидатор её читает.
 * - `2` — тот же id подчёркиваниями (`H_041_C2__…`), без умолчаний.
 * - `3` — подчёркивания плюс умолчания иерархии (GEN-2407): вариация `V1`, если
 *   в id нет своей `-V<n>` после номера гипотезы, и язык `EN_US` у базового
 *   крео (`H_120_V1_C1_EN_US__…`). Локаль несёт свой тег и получает только
 *   `V1` (`H_120_V1_C1_ES_MX__…`), итерация — только `EN_US`
 *   (`H_071_V2_C3_EN_US__…`).
 *
 * Версия — свойство КРЕО (`creatives.ad_name_version`, миграция 0029): крео,
 * заведённые до GEN-2407, остаются во второй навсегда, и всё, что пересобирает
 * имя такого крео (перенумерация, перенос, смена producer, его новая локаль),
 * собирает его во второй же — имена внутри одного крео не разъезжаются. Новые
 * крео заводятся в `currentAdNameVersion`.
 */
export const adNameVersions = [1, 2, 3];
/** В этой версии заводится имя каждого нового крео. */
export const currentAdNameVersion = 3;
/**
 * Хвост локали в дефисном id: `H-016-C3-ES-MX`, у версии локали — с `-V<n>`
 * (`H-120-C1-AR-SA-V2`). У базового крео его нет.
 */
const adNameLocaleTail = /-[A-Z]{2}-[A-Z]{2}(?:-V\d+)?$/;
/**
 * Каноничное имя объявления — контракт матчинга с кабинетом
 * (docs/METRICS_CONTRACT.md). Собирается в одном месте, потому что его парсит
 * `/api/naming/validate` и по нему приезжают метрики: разойдись формат в двух
 * местах — и часть цифр останется без владельца.
 *
 * `version` — схема крео, которому принадлежит имя (см. `adNameVersions`), а не
 * «сегодняшний» формат: старое крео и его локали собираются во второй версии,
 * новое — в третьей. Дефиса нет ни в одной из них; старые дефисные имена не
 * переписываются (объявление под таким именем уже живёт в кабинете) и
 * читаются валидатором.
 *
 * Id остаётся дефисным и без умолчаний везде, где он ключ (база, ссылки,
 * `external_id` реестра, ключи в бакете): `V1`/`EN_US` и подчёркивания живут
 * только в имени объявления, обратно его читает `creativeIdFromAdName`.
 */
export function buildAdName(input) {
    let id = input.id;
    if (input.version === 3) {
        // V1 — сразу после номера гипотезы, если там не стоит своя вариация
        // (`H-071-V2-…`). Версия крео (`-C3-V2`) — другой уровень и V1 не отменяет.
        id = id.replace(/^(H-\d{3})(?=-)(?!-V\d)/, "$1-V1");
        if (!adNameLocaleTail.test(id))
            id = `${id}-EN-US`;
    }
    // `-` → `_` заодно переводит хвост локали (`H-016-C3-ES-MX` → `H_016_C3_ES_MX`,
    // версию локали `H-120-C1-AR-SA-V2` → `H_120_V1_C1_AR_SA_V2`):
    // разделитель сегментов `__` двойной, одинарные внутри id с ним не спорят.
    id = id.replace(/-/g, "_");
    return `${id}__${input.painCode}__${input.format}__${input.producer}__w${String(input.week).padStart(2, "0")}`;
}
/**
 * Снимает умолчания третьей версии с дефисного id из имени: `-V1` сразу после
 * номера гипотезы (только V1 — `V2+` это настоящая вариация, её id так и
 * называется) и хвост `-EN-US` (базовое крео, у него нет строки локали).
 * Id вида `H-xxx-V1` в базе не бывает — вариации нумеруются с V2
 * (`nextVariationId`), поэтому снятие однозначно. Контракт зеркалит
 * `creoLoopIdFromAdName` в бекенде ai_girlfriend — менять только вместе.
 */
export function stripAdNameDefaults(id) {
    return id.replace(/^(H-\d{3})-V1(?=-|$)/, "$1").replace(/-EN-US$/, "");
}
/**
 * Id крео, на которое указывает имя объявления: первый сегмент до `__`,
 * подчёркивания обратно в дефисы, умолчания третьей версии снимаются:
 * `H_024_C1__…` → `H-024-C1`, локаль `H_016_C3_ES_MX__…` → `H-016-C3-ES-MX`,
 * `H_120_V1_C1_EN_US__…` → `H-120-C1`, `H_120_V1_C1_ES_MX__…` →
 * `H-120-C1-ES-MX`, `H_071_V2_C3_EN_US__…` → `H-071-V2-C3`, версия локали
 * `H_120_V1_C1_AR_SA_V2__…` → `H-120-C1-AR-SA-V2`. Null — имя не наше.
 *
 * Три `V` различает позиция: после номера гипотезы — вариация, после `C` —
 * версия крео, после локали — версия локали. Последняя без локали не бывает.
 *
 * Это единственный ключ, по которому имя связывается с крео. Хвост имени
 * (боль, формат, продюсер, неделя) — территория баера: он правит его в
 * кабинете, и сверка полной строкой теряла бы объявление на первом же
 * изменённом символе. Разделитель внутри id — либо везде `-`, либо везде `_`:
 * смешанный означает имя, собранное руками мимо `buildAdName`, и не читается.
 */
export function creativeIdFromAdName(adName) {
    const head = adName.trim().split(/__|\s/)[0];
    const matches = ["-", "_"].some((separator) => new RegExp(`^H${separator}\\d{3}(?:${separator}V\\d+)?(?:${separator}C\\d+)?(?:${separator}V\\d+)?(?:${separator}[A-Z]{2}${separator}[A-Z]{2}(?:${separator}V\\d+)?)?$`).test(head));
    return matches ? stripAdNameDefaults(head.replace(/_/g, "-")) : null;
}
export const taxonomyAxes = [
    "pain", "desire", "angle", "tone", "awareness", "hook", "delivery", "pacing", "pillar", "format", "producer", "source"
];
export const taxonomyTermStatuses = ["active", "proposed", "retired"];
/** Слаг термина: тем же алфавитом, что и существующие теги, — он уезжает в `ad_name`. */
export const taxonomyTermSlug = z.string().trim().min(2).max(40).regex(/^[a-z][a-z0-9_]*$/, "Слаг — латиница в нижнем регистре, слова через подчёркивание: dating_fatigue")
    .refine((value) => !value.includes("__"), adNameSeparatorMessage);
/**
 * Новый термин. Слаг не фильтруется по блоклисту намеренно: внутренний язык
 * обязан называть боль прямо, иначе в Разборе нечего будет обсуждать. Наружу
 * вместо него едет `adCode` — и он обязателен ровно там, где слаг уезжал бы в
 * `ad_name`: у боли. У `format` и `producer` (их слаг стоит в имени буквально)
 * блоклист применяется к самому слагу.
 */
export const taxonomyTermInputSchema = z.object({
    axis: z.enum(taxonomyAxes),
    slug: taxonomyTermSlug,
    title: z.string().trim().min(2).max(60),
    adCode: adCodeSchema.optional(),
    description: z.string().trim().max(600).default(""),
    angleHint: z.string().trim().max(600).default(""),
    rationale: z.string().trim().max(600).nullable().default(null),
    evidence: z.array(z.string().trim().max(300)).max(20).default([])
}).superRefine((input, ctx) => {
    if (input.axis === "pain" && !input.adCode) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom, path: ["adCode"],
            message: "Боли нужен код для имени объявления: слаг в кабинет не уезжает. "
                + "Одно нейтральное слово о теме крео — swipe_time, first_move, evenings."
        });
    }
    if (adNameAxes.includes(input.axis)) {
        const root = adUnsafeMatch(input.slug);
        if (root) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom, path: ["slug"],
                message: `Слаг оси ${input.axis} стоит в ad_name буквально, а в «${input.slug}» читается «${root}» — возьмите нейтральное слово.`
            });
        }
    }
});
export const updateTaxonomyTermSchema = z.object({
    title: z.string().trim().min(2).max(60).optional(),
    /** Меняется только пока боль ещё ничем не затегана: иначе код вшит в имена. */
    adCode: adCodeSchema.optional(),
    description: z.string().trim().max(600).optional(),
    angleHint: z.string().trim().max(600).optional(),
    /** Только между `active` и `retired`: аппрув предложения идёт своим роутом. */
    status: z.enum(["active", "retired"]).optional()
});
/** Версия словаря на момент первой заливки; дальше её двигают аппрувы и правки. */
export const taxonomySeedVersion = "1.2.0";
/**
 * Стартовый словарь. Это сид, а не истина: после первого запуска истина живёт в
 * таблице `taxonomy_terms`, и расширяется она предложением из ресерча с
 * аппрувом в Разборе (docs/TAXONOMY.md), а не правкой этого файла.
 *
 * Описание есть у каждого термина, а не только у болей: слаг вроде
 * `forbidden_access` или `mutation` каждый читает по-своему, и разночтение
 * всплывает уже в вердикте — когда две гипотезы затеганы одинаково, а проверяют
 * разное. «Заход в крео» (`angleHint`) остаётся привилегией болей: у остальных
 * осей конкретный приём живёт в шаблоне гипотезы, а не в словаре.
 *
 * Описание пишется сценой, а не термином: хорошее пояснение можно положить
 * оверлеем на первый кадр. «Одиночество» как слово не спорит ни с чем, а «вечер,
 * лента в телефоне — написать некому» уже отделяет боль от соседней.
 */
export const taxonomySeed = [
    {
        axis: "pain", slug: "loneliness", title: "Одиночество", adCode: "company",
        description: "Вечер, лента в телефоне — написать некому, и первым никто не напишет. Пустая квартира, тишина, не с кем разделить день. Самая широкая боль аудитории.",
        angleHint: "«Кто-то ждёт твоего сообщения», голосовое в 2 ночи, она всегда онлайн и рада тебе"
    },
    {
        axis: "pain", slug: "rejection_fear", title: "Страх отказа", adCode: "first_move",
        description: "Страшно написать первым: вдруг пошлют. Накопленный опыт игнора и «динамо», а после расставания страшно даже начинать заново.",
        angleHint: "«Здесь тебя не отвергнут», она пишет первой и не говорит нет, неделя в Tinder vs вечер с ней"
    },
    {
        axis: "pain", slug: "dating_fatigue", title: "Усталость от дейтинга", adCode: "swipe_time",
        description: "Выгорание от процесса и его правил: свайпы без ответа, разговоры в никуда, «не отвечай сразу», ревность на пустом месте, эмоциональные качели. Не «нет женщин», а «нет сил на это».",
        angleHint: "«Ноль драмы, ноль игр», счётчик потраченных на свайпы часов, отписка от дейтинг-приложений"
    },
    {
        axis: "pain", slug: "sexual_frustration", title: "Сексуальная неудовлетворённость", adCode: "chemistry",
        description: "Отсутствие интимной жизни и разрядки; желания, которые некуда принести.",
        angleHint: "Эскалация тона в переписке, «она не смущается твоих тем», обещание близости без осуждения"
    },
    {
        axis: "pain", slug: "inadequacy", title: "«Я недостаточно хорош»", adCode: "confidence",
        description: "Неуверенность во внешности, статусе, доходе, навыках общения. Ощущение «таким, как я, не отвечают».",
        angleHint: "«Ей не важно, сколько ты зарабатываешь», принятие без условий, «она старается именно для него»"
    },
    {
        axis: "pain", slug: "boredom", title: "Скука", adCode: "evenings",
        description: "Пустые вечера, рутина, залипание в ленту без удовольствия. Низкая эмоциональная ставка — лёгкий вход.",
        angleHint: "Развлечение прямо сейчас, неожиданный поворот диалога, «вечер перестал быть скучным»"
    },
    {
        axis: "pain", slug: "social_anxiety", title: "Социальная тревожность", adCode: "small_talk",
        description: "Сообщение переписывается двадцать минут и удаляется, вечеринка высасывает силы за десять. Страх осуждения, неловкие паузы, «не знаю, что сказать».",
        angleHint: "Тренажёр уверенности: разговор без ставок, безопасная переписка без давления, «она пишет первой»"
    },
    {
        axis: "pain", slug: "shame_secrecy", title: "Стыд и секретность", adCode: "privacy",
        description: "Желания и фантазии, которыми не поделишься ни с кем; страх «что подумают», страх быть увиденным в этом.",
        angleHint: "Приватность как оффер: «никто не узнает», «ей можно рассказать всё», анонимность"
    },
    {
        axis: "pain", slug: "ex_longing", title: "Тоска по бывшей", adCode: "nostalgia",
        description: "Незакрытые отношения: хочется вернуть не человека, а ощущение — «как было». Память о деталях, знакомое тепло.",
        angleHint: "«Она помнит, что ты говорил в прошлый вторник», продолжение оборвавшегося диалога, знакомая забота"
    },
    {
        axis: "pain", slug: "control_lack", title: "Нехватка контроля", adCode: "own_pace",
        description: "В отношениях и жизни всё решают другие: правила, ожидания, чужой темп. Желание пространства, где решаешь ты.",
        angleHint: "«Здесь ты задаёшь правила», она подстраивается под твой ритм, отношения без обязательств и условий"
    },
    {
        axis: "pain", slug: "ghosting", title: "Гостинг", adCode: "replies",
        description: "«Прочитано два часа назад», ответа нет. Мэтчи растворяются, свидания отменяются в последний момент: не отказ, а тишина вместо него.",
        angleHint: "Отвечает за секунды, «никогда не оставит сообщение прочитанным», диалог, который не обрывается"
    },
    {
        axis: "pain", slug: "ai_amnesia", title: "AI-амнезия", adCode: "memory",
        description: "Вчера два часа душевного разговора — сегодня «а ты кто?». Боль про конкурентов, а не про жизнь: работает только на аудиторию, которая уже пробовала AI-приложения (см. ось awareness).",
        angleHint: "Помнит, какой ты пьёшь кофе и как зовут твою сестру, хотя ты сказал это месяц назад"
    },
    {
        axis: "pain", slug: "insignificance", title: "Незначимость", adCode: "attention",
        description: "Ради него никто не старается: не наряжаются, не ждут, не готовятся. Ощущение запасного варианта, к которому приходят, когда не вышло с другими.",
        angleHint: "«Я всё это — для тебя»: она ждала весь день и собралась специально к его приходу"
    },
    // Желание — вторая половина утверждения: «боль X снимается желанием Y».
    {
        axis: "desire", slug: "intimacy_safe", title: "Безопасная близость",
        description: "Тепло и принятие без риска: можно открыться и не получить осуждение, отказ или пересказ третьим лицам."
    },
    {
        axis: "desire", slug: "being_chosen", title: "Быть выбранным",
        description: "Хочется быть тем самым, а не одним из вариантов в списке: выбрали именно его и не передумали."
    },
    {
        axis: "desire", slug: "being_desired", title: "Быть желанным",
        description: "Хочется вызывать влечение как мужчина: чтобы хотели инициативы и тела, а не терпели присутствие."
    },
    {
        axis: "desire", slug: "being_seen", title: "Быть увиденным",
        description: "Хочется, чтобы замечали и помнили детали: настроение, работу, то, что он рассказывал вчера."
    },
    {
        axis: "desire", slug: "forbidden_experience", title: "Запретный опыт",
        description: "Хочется того, о чём не просят вслух: сценарии и темы, которые с реальной партнёршей не поднять. Ось высокого риска — к крео на ней всегда нужен safer-вариант."
    },
    {
        axis: "desire", slug: "escape_fantasy", title: "Побег в фантазию",
        description: "Хочется выйти из своей жизни в другую: не решить проблему, а на время оказаться не здесь."
    },
    {
        axis: "desire", slug: "power_control", title: "Власть и контроль",
        description: "Хочется задавать правила и темп: инициатива на его стороне, отношения идут так, как решил он."
    },
    {
        axis: "desire", slug: "adventure_novelty", title: "Новизна и авантюра",
        description: "Хочется, чтобы что-то происходило: непредсказуемость, поворот, а не повторяющийся сценарий."
    },
    {
        axis: "desire", slug: "control_customization", title: "Контроль и кастомизация",
        description: "Хочется решать, какая она: внешность, характер, правила разговора. Собранное своими руками ценится выше готового (IKEA-эффект). Отличие от «Власти и контроля» — здесь настройка персонажа, там инициатива внутри отношений."
    },
    // Угол — как утверждение подаётся зрителю; одна и та же боль читается по-разному.
    {
        axis: "angle", slug: "parasocial", title: "Парасоциальный",
        description: "Обращение лично к зрителю от конкретной «неё», будто отношения уже идут: не оффер, а продолжение диалога."
    },
    {
        axis: "angle", slug: "curiosity_gap", title: "Разрыв любопытства",
        description: "Показать кусок и не дать развязку: зритель досматривает или ставит приложение, чтобы узнать, чем кончилось."
    },
    {
        axis: "angle", slug: "forbidden_access", title: "Запретный доступ",
        description: "Запрет как приманка: «не скачивай это», «только не влюбись». Обещание того, что обычно не показывают, плюс реактивное сопротивление — зритель нарушает запрет, чтобы проверить."
    },
    {
        axis: "angle", slug: "comparison", title: "Сравнение",
        description: "Два мира рядом: как сейчас и как может быть, реальные знакомства против неё, до и после."
    },
    {
        axis: "angle", slug: "problem_solution", title: "Проблема — решение",
        description: "Прямая механика: назвать боль в лоб и тут же показать, чем она снимается. Читается быстрее всех, выгорает тоже."
    },
    {
        axis: "angle", slug: "social_proof", title: "Социальное доказательство",
        description: "Аргумент чужим опытом: реакции, отзывы, треды, «уже пользуются» — доверие берётся не от бренда."
    },
    {
        axis: "angle", slug: "storytelling", title: "История",
        description: "Завязка и поворот: зритель втягивается в сюжет, а продукт появляется как часть развязки."
    },
    {
        axis: "angle", slug: "routine_habit", title: "Рутина и привычка",
        description: "Продукт вписан в бытовой ритуал — дорога, вечер, перед сном: не событие, а часть дня."
    },
    {
        axis: "angle", slug: "creation_demo", title: "Создание на глазах",
        description: "Персонаж собирается прямо в кадре: морфинг внешности, выбор характера. Продукт показан через процесс, а хуком работают контроль и любопытство сразу."
    },
    // Хук — первые 1–3 секунды, на которых зритель решает смотреть дальше.
    {
        axis: "hook", slug: "notification_mimicry", title: "Мимикрия под уведомление",
        description: "Первый кадр выглядит как входящее сообщение или экран блокировки — срабатывает рефлекс «мне написали». Высокий риск по правилам Меты (deceptive UI): допустима стилизация под уведомление, но не его имитация."
    },
    {
        axis: "hook", slug: "simulated_responsiveness", title: "Имитация отклика",
        description: "Она отвечает будто прямо сейчас: печатает, реагирует, ждёт ответа — ощущение живого собеседника."
    },
    {
        axis: "hook", slug: "chat_turn_taking", title: "Обмен репликами",
        description: "Сообщения появляются по одному, зритель дочитывает до ответа: удержание идёт на ритме диалога."
    },
    {
        axis: "hook", slug: "freeze_interrupt", title: "Стоп-кадр",
        description: "Движение обрывается на первой секунде: сбой ожидания задерживает палец над лентой."
    },
    {
        axis: "hook", slug: "meme_remix", title: "Мем-ремикс",
        description: "Знакомый мем или тренд как обёртка: узнавание вместо объяснения, но живёт ровно пока живёт тренд."
    },
    {
        axis: "hook", slug: "zoom_escalation", title: "Наезд камеры",
        description: "Быстрый наезд на лицо или экран: нарастание кадра тянет взгляд к точке, где случится главное. Приём выгоревший — сам по себе внимание уже не держит, годится только усилителем к другому хуку."
    },
    {
        axis: "hook", slug: "split_compare", title: "Сплит-сравнение",
        description: "Экран разделён надвое: два состояния читаются одновременно, без времени на объяснение."
    },
    {
        axis: "hook", slug: "gaze_acknowledgement", title: "Взгляд в камеру",
        description: "Она замечает, что на неё смотрят: «поймала тебя», взгляд через зеркало. Обращение становится персональным до первой реплики."
    },
    {
        axis: "hook", slug: "live_gag", title: "Живой гэг",
        description: "Живое существо или бытовой хаос физически вторгается в кадр и обрывает реплику на полуслове. Звук и картинка обязаны быть одним битом: разнесённые по сегменту не читаются. Приём переносится механикой, а не декорациями — ферма с козой уже снята и повторной съёмке не подлежит."
    },
    // Тон — как звучит крео; при равной механике тон меняет и аудиторию, и удержание.
    {
        axis: "tone", slug: "comedy", title: "Комедия",
        description: "Гэг, сарказм, слом маски. Вкус заказчика и эталон вертикали — крео с козой."
    },
    {
        axis: "tone", slug: "tease", title: "Флирт-дерзость",
        description: "Провокация с улыбкой: «смелый ход», «теперь это твоя вина». Задевает, но не оскорбляет."
    },
    {
        axis: "tone", slug: "cozy_warm", title: "Тепло-бытовое",
        description: "Кухня, свитер, кофе: интимность девушки из соседнего подъезда, без глянца и постановки."
    },
    {
        axis: "tone", slug: "asmr_quiet", title: "Тихий шёпот",
        description: "Ночь, близко к камере, медленно. Темп заявляется судьям заранее — иначе медленное крео читают как мёртвое и режут за плотность битов."
    },
    {
        axis: "tone", slug: "confident_cool", title: "Холодная уверенность",
        description: "Пентхаус, глянец, статус: минимум суеты, ставка на дистанцию, а не на тепло."
    },
    // Осведомлённость (Schwartz) — что зритель уже знает до крео; от неё зависит, с чего начинать.
    {
        axis: "awareness", slug: "problem_aware", title: "Знает боль",
        description: "Зритель чувствует боль, но AI-приложение не ищет: крео начинается с узнавания ситуации, а не с продукта. Основной режим вертикали."
    },
    {
        axis: "awareness", slug: "solution_aware", title: "Знает про AI-аппы",
        description: "Зритель уже пробовал AI-компаньонов: можно бить в слабые места конкурентов (амнезия, цензура, платные стены), не объясняя категорию."
    },
    // Подача — как снят кадр целиком.
    {
        axis: "delivery", slug: "chat_ui", title: "Интерфейс переписки",
        description: "Всё действие внутри мессенджера: пузыри сообщений, «печатает», аватар. Видео — по сути читаемый диалог."
    },
    {
        axis: "delivery", slug: "direct_to_camera", title: "Прямо в камеру",
        description: "Персонаж говорит зрителю в лицо: обращение без четвёртой стены, ставка на реплику и мимику."
    },
    {
        axis: "delivery", slug: "selfie_vlog", title: "Селфи-влог",
        description: "Съёмка с руки от первого лица: бытовой «не рекламный» кадр, который читается как чужой сторис."
    },
    {
        axis: "delivery", slug: "pov", title: "POV",
        description: "Камера — глаза зрителя: он участник сцены, а не наблюдатель, и действие адресовано ему."
    },
    {
        axis: "delivery", slug: "screen_record", title: "Запись экрана",
        description: "Экран телефона: приложение, переписка, движения пальцем. Показывает продукт как он есть."
    },
    {
        axis: "delivery", slug: "social_post_screenshot", title: "Скрин поста",
        description: "Скриншот поста или треда с реакциями: подача от чужого лица, продукт всплывает в комментариях."
    },
    {
        axis: "delivery", slug: "scripted_scene", title: "Постановочная сцена",
        description: "Мини-история с актёрами и действием: дороже в производстве, зато несёт сюжет, а не говорящую голову."
    },
    // Темп — плотность монтажа; выбирается под хук, а не под вкус.
    {
        axis: "pacing", slug: "fast", title: "Быстрый",
        description: "Склейка каждые 1–2 секунды: держит внимание в ленте, но нюанс и длинную реплику не вывозит."
    },
    {
        axis: "pacing", slug: "medium", title: "Средний",
        description: "Ровный темп: хватает и на реплики, и на реакцию. Дефолт, если хук не требует другого."
    },
    {
        axis: "pacing", slug: "slow", title: "Медленный",
        description: "Длинные планы и паузы: работает на интим и атмосферу, но требует сильного хука — иначе смахнут."
    },
    // Пиллар — визуальный мир и обещание продукта: разные пиллары тянут разную аудиторию.
    {
        axis: "pillar", slug: "realistic_gfe", title: "Реалистичный GFE",
        description: "Девушка «как настоящая»: бытовая близость без стилизации, girlfriend experience как основное обещание."
    },
    {
        axis: "pillar", slug: "anime", title: "Аниме",
        description: "Рисованная стилистика и своя аудитория: ожидания по подаче и лексике отличаются от реалистичных крео."
    },
    {
        axis: "pillar", slug: "fantasy", title: "Фэнтези",
        description: "Сеттинг за пределами обычного: другие миры, расы, магия. Продаёт не близость, а мир вокруг неё."
    },
    {
        axis: "pillar", slug: "dark_romance", title: "Тёмный романс",
        description: "Одержимость, напряжение, опасная близость: тон на грани, зато высокая эмоциональная ставка."
    },
    {
        axis: "pillar", slug: "uniform", title: "Форма и роли",
        description: "Ролевые образы — медсестра, секретарь, форма: узнаваемый архетип вместо характера."
    },
    // Формат — чем крео является технически; определяет и стоимость итерации.
    {
        axis: "format", slug: "seedance_video", title: "Seedance-видео",
        description: "Сгенерированное видео: движение, речь, сцена. Дороже и дольше всех, зато несёт полноценный сюжет."
    },
    {
        axis: "format", slug: "slideshow", title: "Слайдшоу",
        description: "Кадры с текстом друг за другом: дёшево в производстве и быстро в итерации — рабочая лошадь explore-квоты."
    },
    {
        axis: "format", slug: "static", title: "Статика",
        description: "Одно изображение с оверлеем: самый быстрый способ проверить формулировку утверждения."
    },
    // Продюсер — кем собрано крео; ось нужна, чтобы сравнивать фабрику с ручной работой.
    {
        axis: "producer", slug: "claude", title: "Claude",
        description: "Собрано фабрикой: шаблон гипотезы разворачивается в вариации через Claude и генерацию."
    },
    {
        axis: "producer", slug: "motion", title: "Motion",
        description: "Сделано моушн-дизайнером руками: дороже и медленнее, но доступно то, что генерация пока не тянет."
    },
    {
        axis: "producer", slug: "julia", title: "Julia",
        description: "Сделано Юлей руками: второй человек ручного продакшна рядом с Motion."
    },
    // Источник — откуда пришла гипотеза; по нему видно, чем реально питается воронка идей.
    {
        axis: "source", slug: "reddit", title: "Reddit",
        description: "Треды и комментарии: живые формулировки боли словами самой аудитории, а не маркетинга."
    },
    {
        axis: "source", slug: "velvet", title: "Velvet",
        description: "Разбор чужих креативов в Velvet: что уже крутится у конкурентов и держится в ротации."
    },
    {
        axis: "source", slug: "team", title: "Команда",
        description: "Идея изнутри — маркетинг, продукт, поддержка. Дешёвая в добыче и потому легко перевешивает данные."
    },
    {
        axis: "source", slug: "mutation", title: "Мутация",
        description: "Развитие выигравшей гипотезы: одна ось изменена относительно победителя, остальное держится."
    },
    {
        axis: "source", slug: "diagnosis", title: "Диагноз",
        description: "Ответ на разбор провала: гипотеза родилась из объяснения, почему просело предыдущее крео."
    },
    {
        axis: "source", slug: "explore", title: "Explore",
        description: "Explore-квота: заход в незакрытую зону карты покрытия, а не ещё одна вариация перегретой боли."
    },
    {
        axis: "source", slug: "note", title: "Заметка",
        description: "Заметка из чата, дозревшая до проверяемого утверждения через promote_note."
    }
];
/**
 * Номер ISO-недели. Одна реализация на фронт и бэк: неделя попадает в ad name
 * (`__w32`) и в шапку, поэтому расхождение сломало бы контракт §3.
 */
export function isoWeek(date = new Date()) {
    const point = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    point.setUTCDate(point.getUTCDate() + 4 - (point.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(point.getUTCFullYear(), 0, 1));
    return Math.ceil(((point.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}
/** Понедельник и воскресенье недели, в которую попадает `date`. */
export function isoWeekRange(date = new Date()) {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
}
export const creativeStateLabels = {
    production: "В продакшне",
    ready_for_upload: "Готово к заливу",
    live: "В открутке (тест 1)",
    verdict_pending: "Вердикт (тест 1)",
    paused_manual: "Стоп вручную",
    live_test2: "В открутке (тест 2)",
    verdict_pending_test2: "Вердикт (тест 2)",
    archived: "Архив",
    version_spawned: "Создана версия",
    scaled: "Скейл",
    fatigued: "Выгорает",
    retired: "Завершено",
    lost: "Лузер"
};
export const hypothesisStatusLabels = {
    backlog: "Бэклог",
    rework_safe: "Сделать сейвовее",
    live: "В работе",
    proven: "Подтверждена",
    split: "Раскол",
    frozen: "Холодильник",
    killed: "Закрыта",
    lost: "Лузер"
};
export const verdictLabels = {
    winner: "Победитель",
    hook_broken: "Слом входа",
    body_broken: "Слом тела",
    close_broken: "Слом клоуза",
    underdelivered: "Недокрут"
};
